// LumiDrips — drip sequence builder + active runs dashboard.
// Pulled out as its own file so LumiDashboard.js doesn't get any heavier.
//
// Sequences = templates (e.g. "Slow nurture: Day 0 / 7 / 14")
// Runs      = per-contact progress through a sequence (active/paused/completed)
//
// Why this matters: Louise wants to set up follow-ups once and have them
// auto-send. When the contact replies, her drip auto-pauses (handled by the
// inbox poller) so she can decide what to do.
import { useEffect, useState } from "react";
import { lumiFetch, fmtDateTime } from "./lumiApi";
import { toast } from "./lumiToast";

const STATUS_COLOURS = {
  active:    "bg-emerald-300/20 border-emerald-300/40 text-emerald-100",
  paused:    "bg-amber-300/20 border-amber-300/40 text-amber-100",
  completed: "bg-sky-300/20 border-sky-300/40 text-sky-100",
  stopped:   "bg-stone-400/20 border-stone-400/40 text-stone-200",
  failed:    "bg-rose-300/20 border-rose-300/40 text-rose-100",
  sending:   "bg-violet-300/20 border-violet-300/40 text-violet-100",
};

const DEFAULT_DELAYS = [0, 7, 14]; // Louise's preferred slow-nurture default

export default function LumiDrips() {
  const [drafts, setDrafts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [runs, setRuns] = useState([]);
  const [editing, setEditing] = useState(null); // sequence object being edited (or null)
  const [showStartFor, setShowStartFor] = useState(null); // sequence object (start picker)
  const [contactQuery, setContactQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const [dr, c, s, r] = await Promise.all([
        lumiFetch("/drafts"),
        lumiFetch("/contacts"),
        lumiFetch("/drip-sequences"),
        lumiFetch("/drip-runs"),
      ]);
      setDrafts(dr || []);
      setContacts(c || []);
      setSequences(s || []);
      setRuns(r || []);
    } catch (err) {
      toast.error(err?.data?.detail || "Could not load drip data");
    }
  };

  useEffect(() => { refresh(); }, []);

  const newSequence = () => {
    setEditing({
      id: null,
      name: "Slow nurture",
      description: "",
      steps: DEFAULT_DELAYS.map((delay_days, i) => ({
        draft_id: "",
        delay_days,
      })),
    });
  };

  const saveSequence = async () => {
    if (!editing) return;
    if (!editing.name.trim()) return toast.error("Give the sequence a name");
    if (!editing.steps.length) return toast.error("Add at least one step");
    if (editing.steps.some((s) => !s.draft_id)) return toast.error("Pick a draft for every step");
    setLoading(true);
    try {
      const body = {
        name: editing.name.trim(),
        description: editing.description || "",
        steps: editing.steps.map((s) => ({
          draft_id: s.draft_id,
          delay_days: Number(s.delay_days) || 0,
        })),
      };
      if (editing.id) {
        await lumiFetch(`/drip-sequences/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
        toast.success(`Saved "${body.name}"`);
      } else {
        await lumiFetch("/drip-sequences", { method: "POST", body: JSON.stringify(body) });
        toast.success(`Created "${body.name}"`);
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const deleteSequence = async (seq) => {
    const stats = seq.run_stats || {};
    const liveCount = (stats.active || 0) + (stats.paused || 0);
    const warning = liveCount
      ? `Delete "${seq.name}"? This will STOP ${liveCount} live run(s) for current contacts. This cannot be undone.`
      : `Delete "${seq.name}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      await lumiFetch(`/drip-sequences/${seq.id}`, { method: "DELETE" });
      toast.success("Sequence deleted");
      refresh();
    } catch (err) {
      toast.error(err?.data?.detail || "Could not delete");
    }
  };

  const startRun = async (sequence_id, contact_id) => {
    try {
      await lumiFetch(`/drip-sequences/${sequence_id}/start`, {
        method: "POST",
        body: JSON.stringify({ sequence_id, contact_id }),
      });
      toast.success("Drip started 🪴");
      setShowStartFor(null);
      setContactQuery("");
      refresh();
    } catch (err) {
      toast.error(err?.data?.detail || "Could not start drip");
    }
  };

  const runAction = async (run_id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Delete this run from the dashboard?")) return;
        await lumiFetch(`/drip-runs/${run_id}`, { method: "DELETE" });
      } else {
        await lumiFetch(`/drip-runs/${run_id}/${action}`, { method: "POST" });
      }
      refresh();
    } catch (err) {
      toast.error(err?.data?.detail || `Could not ${action}`);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 max-w-6xl mx-auto w-full" data-testid="lumi-drips-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-2xl font-bold">🪴 Drip sequences</div>
          <div className="text-xs text-white/50 mt-1">Send a series of follow-up emails on a schedule. Auto-pauses when the contact replies.</div>
        </div>
        <button
          onClick={newSequence}
          data-testid="lumi-new-drip"
          className="px-4 py-2 bg-emerald-300/90 hover:bg-emerald-300 text-emerald-950 rounded-full text-sm font-semibold transition-colors"
        >
          + New sequence
        </button>
      </div>

      {/* Sequences list */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Templates</div>
        {sequences.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 p-6 text-center text-sm text-white/50">
            No drip sequences yet. Click <strong className="text-white/80">+ New sequence</strong> to create one.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {sequences.map((s) => {
              const rs = s.run_stats || {};
              return (
                <div key={s.id} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`lumi-drip-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{s.name}</div>
                      {s.description && <div className="text-xs text-white/50 truncate">{s.description}</div>}
                    </div>
                    <div className="text-[10px] text-white/40 whitespace-nowrap">{s.steps?.length || 0} step{(s.steps?.length || 0) !== 1 ? "s" : ""}</div>
                  </div>
                  {/* Step delays preview */}
                  <div className="text-xs text-white/60 mt-2 flex items-center gap-1.5 flex-wrap">
                    {(s.steps || []).map((st, i) => (
                      <span key={i} className="bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                        Day {(s.steps.slice(0, i + 1).reduce((a, x) => a + (Number(x.delay_days) || 0), 0))}
                      </span>
                    ))}
                  </div>
                  {/* Run stats */}
                  {(rs.active || rs.paused || rs.completed || rs.stopped || rs.failed) ? (
                    <div className="text-[10px] mt-3 flex flex-wrap gap-1.5">
                      {Object.entries(rs).map(([k, v]) => (
                        <span key={k} className={`rounded-full px-2 py-0.5 border ${STATUS_COLOURS[k] || "bg-white/5 border-white/10"}`}>
                          {v} {k}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      onClick={() => setShowStartFor(s)}
                      data-testid={`lumi-drip-start-${s.id}`}
                      className="text-xs px-3 py-1.5 bg-emerald-300/90 hover:bg-emerald-300 text-emerald-950 rounded-full font-semibold"
                    >
                      ▶ Start for contact
                    </button>
                    <button
                      onClick={() => setEditing({ ...s })}
                      data-testid={`lumi-drip-edit-${s.id}`}
                      className="text-xs px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-full"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => deleteSequence(s)}
                      data-testid={`lumi-drip-delete-${s.id}`}
                      className="text-xs px-3 py-1.5 text-rose-200 hover:bg-rose-300/10 rounded-full"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Active runs */}
      <div>
        <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Active &amp; recent runs</div>
        {runs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 p-6 text-center text-sm text-white/50">
            No drips running yet. Start one from a sequence above.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/5 divide-y divide-white/10 overflow-hidden">
            {runs.map((r) => {
              const seq = sequences.find((s) => s.id === r.sequence_id);
              const stepCount = seq?.steps?.length || 0;
              const stepNum = Math.min((r.current_step || 0) + 1, stepCount || 1);
              return (
                <div key={r.id} className="p-3 sm:p-4 flex items-center gap-3 flex-wrap" data-testid={`lumi-drip-run-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.contact_name || r.contact_email}
                      <span className="text-white/40 font-normal"> → {r.sequence_name}</span>
                    </div>
                    <div className="text-xs text-white/50 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>Step {r.current_step + (r.status === "completed" ? 0 : 1)}/{stepCount || "?"}</span>
                      {r.status === "active" && r.next_send_at && <span>· Next {fmtDateTime(r.next_send_at)}</span>}
                      {r.last_sent_at && <span>· Last sent {fmtDateTime(r.last_sent_at)}</span>}
                      {r.paused_reason && <span className="text-amber-300/80">· {r.paused_reason}</span>}
                      {r.error && <span className="text-rose-300/80" title={r.error}>· error</span>}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 font-bold ${STATUS_COLOURS[r.status] || ""}`}>
                    {r.status}
                  </span>
                  <div className="flex gap-1">
                    {r.status === "active" && (
                      <button onClick={() => runAction(r.id, "pause")} data-testid={`lumi-drip-run-pause-${r.id}`} className="text-xs px-2.5 py-1 bg-white/10 hover:bg-amber-300/30 rounded-full">⏸ Pause</button>
                    )}
                    {(r.status === "paused" || r.status === "failed") && (
                      <button onClick={() => runAction(r.id, "resume")} data-testid={`lumi-drip-run-resume-${r.id}`} className="text-xs px-2.5 py-1 bg-emerald-300/30 hover:bg-emerald-300/50 rounded-full">▶ Resume</button>
                    )}
                    {(r.status === "active" || r.status === "paused" || r.status === "failed") && (
                      <button onClick={() => runAction(r.id, "stop")} data-testid={`lumi-drip-run-stop-${r.id}`} className="text-xs px-2.5 py-1 text-rose-200 hover:bg-rose-300/10 rounded-full">■ Stop</button>
                    )}
                    {(r.status === "stopped" || r.status === "completed" || r.status === "failed") && (
                      <button onClick={() => runAction(r.id, "delete")} data-testid={`lumi-drip-run-delete-${r.id}`} className="text-xs px-2.5 py-1 text-white/40 hover:text-rose-300 rounded-full">✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sequence editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="lumi-drip-editor">
            <div className="font-bold text-lg">{editing.id ? "Edit sequence" : "New sequence"}</div>
            <div className="space-y-2">
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Sequence name (e.g. School welcome)"
                data-testid="lumi-drip-name"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
              />
              <textarea
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Optional notes (only you see these)"
                rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 resize-y"
              />
            </div>
            <div className="text-xs uppercase tracking-widest text-white/40 mt-3">Steps</div>
            <div className="space-y-2">
              {editing.steps.map((step, i) => {
                const cumulative = editing.steps.slice(0, i + 1).reduce((a, x) => a + (Number(x.delay_days) || 0), 0);
                return (
                  <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-2 flex-wrap">
                    <span className="text-xs text-white/50 shrink-0 w-6 text-center font-bold">#{i + 1}</span>
                    <select
                      value={step.draft_id}
                      onChange={(e) => {
                        const next = [...editing.steps];
                        next[i] = { ...next[i], draft_id: e.target.value };
                        setEditing({ ...editing, steps: next });
                      }}
                      data-testid={`lumi-drip-step-draft-${i}`}
                      className="flex-1 min-w-[140px] bg-[#0a0a0a] border border-white/10 rounded px-2 py-1 text-xs focus:outline-none focus:border-white/30"
                    >
                      <option value="">Pick a draft…</option>
                      {drafts.map((d) => (
                        <option key={d.id} value={d.id}>{d.subject || d.title || "(no subject)"}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-white/50">+</span>
                      <input
                        type="number"
                        min="0"
                        value={step.delay_days}
                        onChange={(e) => {
                          const next = [...editing.steps];
                          next[i] = { ...next[i], delay_days: Number(e.target.value) || 0 };
                          setEditing({ ...editing, steps: next });
                        }}
                        data-testid={`lumi-drip-step-delay-${i}`}
                        className="w-14 bg-[#0a0a0a] border border-white/10 rounded px-1 py-1 text-xs focus:outline-none focus:border-white/30 text-center"
                      />
                      <span className="text-white/50">days</span>
                      <span className="text-white/30 ml-1">(Day {cumulative})</span>
                    </div>
                    <button
                      onClick={() => {
                        const next = editing.steps.filter((_, j) => j !== i);
                        setEditing({ ...editing, steps: next });
                      }}
                      title="Remove step"
                      className="text-white/30 hover:text-rose-300 px-1.5"
                    >✕</button>
                  </div>
                );
              })}
              <button
                onClick={() => setEditing({ ...editing, steps: [...editing.steps, { draft_id: "", delay_days: 7 }] })}
                data-testid="lumi-drip-add-step"
                className="w-full text-xs px-3 py-2 border border-dashed border-white/20 rounded-lg text-white/60 hover:text-white hover:border-white/40 transition-colors"
              >
                + Add step
              </button>
            </div>
            <div className="text-[11px] text-white/40 italic">
              Step #1 sends with the delay you set after starting the run (0 = immediately). Each later step waits its delay AFTER the previous step sends.
            </div>
            <div className="pt-2 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button
                onClick={saveSequence}
                disabled={loading}
                data-testid="lumi-drip-save"
                className="px-4 py-2 bg-emerald-300 text-emerald-950 rounded-full text-sm font-semibold hover:bg-emerald-200 disabled:opacity-50"
              >
                {loading ? "Saving…" : editing.id ? "Save changes" : "Create sequence"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start-for-contact picker */}
      {showStartFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowStartFor(null); setContactQuery(""); } }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" data-testid="lumi-drip-start-modal">
            <div className="px-5 py-3 border-b border-white/10">
              <div className="font-bold">Start "{showStartFor.name}" for…</div>
              <input
                type="search"
                placeholder="🔍 Search contacts…"
                value={contactQuery}
                onChange={(e) => setContactQuery(e.target.value)}
                autoFocus
                data-testid="lumi-drip-start-search"
                className="w-full bg-white/5 border border-white/10 rounded-full px-3 py-1.5 mt-2 text-xs focus:outline-none focus:border-white/30"
              />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-white/5">
              {(() => {
                const q = contactQuery.trim().toLowerCase();
                const filtered = contacts.filter((c) => c.subscribed !== false && (
                  !q || (c.email || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q)
                ));
                if (filtered.length === 0) {
                  return <div className="p-6 text-center text-xs text-white/40">No subscribed contacts match.</div>;
                }
                return filtered.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => startRun(showStartFor.id, c.id)}
                    data-testid={`lumi-drip-start-pick-${c.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-white/5"
                  >
                    <div className="text-sm font-medium truncate">{c.name || c.email}</div>
                    {c.name && <div className="text-xs text-white/50 truncate">{c.email}</div>}
                  </button>
                ));
              })()}
            </div>
            <div className="px-5 py-3 border-t border-white/10 text-right">
              <button onClick={() => { setShowStartFor(null); setContactQuery(""); }} className="text-xs text-white/60 hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
